import React, { useState, useEffect, useCallback } from 'react';
import { Package, Phone, MapPin, Loader2, RotateCcw, CheckCircle, Camera, Warehouse } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const STATUS_META = {
  pickup_assigned: { label: 'To Pick Up', cls: 'bg-blue-500' },
  picked_up: { label: 'Picked Up', cls: 'bg-indigo-500' },
  in_transit: { label: 'In Transit', cls: 'bg-cyan-500' },
  delivered: { label: 'Delivered', cls: 'bg-green-500' },
};

const formatAddress = (addr) => {
  if (!addr) return null;
  if (typeof addr === 'string') return addr;
  return [addr.address1, addr.address2, addr.city, addr.province, addr.country]
    .filter(Boolean).join(', ');
};

const ReturnPickupCard = ({ pickup, onUpdated }) => {
  const [photoUrl, setPhotoUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const status = pickup.status;
  const meta = STATUS_META[status] || { label: status, cls: 'bg-gray-500' };
  const address = formatAddress(pickup.pickup_address);

  const handlePhotoUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API_BASE_URL}/v1/returns/portal/upload-photo`, formData);
      setPhotoUrl(res.data.url);
      toast.success('Photo uploaded');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Photo upload failed');
    } finally {
      setUploading(false);
    }
  };

  const updateStatus = async (newStatus) => {
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_BASE_URL}/v1/returns/${pickup.id}/driver-status`,
        { status: newStatus, photo_url: photoUrl || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(newStatus === 'picked_up' ? 'Marked as picked up' : 'Delivered to warehouse');
      onUpdated();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update status');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-wt-surface rounded-2xl p-4 border border-wt-border" data-testid={`return-pickup-card-${pickup.return_number}`}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-lg font-bold text-wt-gold flex items-center gap-2">
          <RotateCcw className="w-4 h-4" />
          {pickup.return_number}
        </span>
        <div className={`${meta.cls} text-white px-3 py-1 rounded-full text-sm font-semibold`}>
          {meta.label}
        </div>
      </div>

      <p className="text-base font-medium text-wt-text-primary mb-1">{pickup.customer_name || 'Customer'}</p>
      {address && (
        <button
          onClick={() => window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`, '_blank')}
          className="flex items-start gap-2 w-full text-left mb-2 active:scale-98"
          data-testid={`return-pickup-address-${pickup.return_number}`}
        >
          <MapPin className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
          <span className="text-sm text-blue-400">{address}</span>
        </button>
      )}
      {pickup.customer_phone && (
        <a href={`tel:${pickup.customer_phone}`} className="flex items-center gap-2 mb-3" data-testid={`return-pickup-phone-${pickup.return_number}`}>
          <Phone className="w-4 h-4 text-green-400" />
          <span className="text-sm text-green-400 font-medium">{pickup.customer_phone}</span>
        </a>
      )}

      <div className="space-y-1.5 mb-3">
        {(pickup.items || []).map((item, idx) => (
          <div key={idx} className="flex items-center gap-2 p-2 bg-wt-bg rounded-lg">
            <Package className="w-4 h-4 text-wt-text-muted flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm text-wt-text-primary truncate">{item.product_name || item.name || item.sku}</p>
              <p className="text-xs text-wt-text-muted">Qty: {item.quantity_returning || item.quantity || 1}{item.sku ? ` · ${item.sku}` : ''}</p>
            </div>
          </div>
        ))}
      </div>

      {pickup.pickup_notes && (
        <p className="text-xs text-amber-400 mb-3">Note: {pickup.pickup_notes}</p>
      )}
      {pickup.scheduled_pickup_date && status === 'pickup_assigned' && (
        <p className="text-xs text-wt-text-muted mb-3">Scheduled: {pickup.scheduled_pickup_date}</p>
      )}

      {status === 'pickup_assigned' && (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            {photoUrl ? (
              <img src={photoUrl} alt="pickup proof" className="w-14 h-14 object-cover rounded-lg border border-green-500/40" />
            ) : (
              <label className="flex items-center gap-2 px-3 py-2.5 rounded-xl border-2 border-dashed border-wt-border text-wt-text-muted text-sm cursor-pointer active:scale-98 flex-1 justify-center" data-testid={`pickup-photo-label-${pickup.return_number}`}>
                <input type="file" accept="image/*" capture="environment" className="hidden"
                  onChange={(e) => handlePhotoUpload(e.target.files?.[0])} />
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
                {uploading ? 'Uploading...' : 'Take pickup photo (required)'}
              </label>
            )}
          </div>
          <button
            onClick={() => updateStatus('picked_up')}
            disabled={!photoUrl || submitting}
            className={`w-full py-3 rounded-xl font-semibold text-base flex items-center justify-center gap-2 ${photoUrl ? 'bg-indigo-600 text-white active:scale-98' : 'bg-wt-bg text-wt-text-muted'}`}
            data-testid={`mark-picked-up-btn-${pickup.return_number}`}
          >
            {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <CheckCircle className="w-5 h-5" />}
            Mark Picked Up
          </button>
        </div>
      )}

      {(status === 'picked_up' || status === 'in_transit') && (
        <button
          onClick={() => updateStatus('delivered')}
          disabled={submitting}
          className="w-full py-3 rounded-xl font-semibold text-base bg-green-600 text-white flex items-center justify-center gap-2 active:scale-98"
          data-testid={`mark-delivered-btn-${pickup.return_number}`}
        >
          {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Warehouse className="w-5 h-5" />}
          Delivered to Warehouse
        </button>
      )}

      {status === 'delivered' && (
        <div className="flex items-center gap-2 text-green-400 text-sm font-medium" data-testid={`pickup-done-${pickup.return_number}`}>
          <CheckCircle className="w-4 h-4" /> Dropped at warehouse — awaiting QC
        </div>
      )}
    </div>
  );
};

export const DriverReturnPickups = ({ onCountChange }) => {
  const [pickups, setPickups] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchPickups = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_BASE_URL}/v1/returns/driver/my-pickups`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPickups(res.data.pickups || []);
      if (onCountChange) onCountChange(res.data.active_count || 0);
    } catch (e) {
      toast.error('Failed to load return pickups');
    } finally {
      setLoading(false);
    }
  }, [onCountChange]);

  useEffect(() => { fetchPickups(); }, [fetchPickups]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-10 h-10 animate-spin text-wt-accent" />
      </div>
    );
  }

  if (pickups.length === 0) {
    return (
      <div className="text-center py-20" data-testid="no-return-pickups">
        <RotateCcw className="w-16 h-16 text-wt-text-muted mx-auto mb-4" />
        <p className="text-xl text-wt-text-muted">No return pickups assigned</p>
      </div>
    );
  }

  const active = pickups.filter(p => ['pickup_assigned', 'picked_up', 'in_transit'].includes(p.status));
  const done = pickups.filter(p => !['pickup_assigned', 'picked_up', 'in_transit'].includes(p.status));

  return (
    <div className="space-y-3" data-testid="driver-return-pickups">
      {active.map(p => <ReturnPickupCard key={p.id} pickup={p} onUpdated={fetchPickups} />)}
      {done.length > 0 && (
        <>
          <p className="text-sm text-wt-text-muted pt-2">Completed</p>
          {done.map(p => <ReturnPickupCard key={p.id} pickup={p} onUpdated={fetchPickups} />)}
        </>
      )}
    </div>
  );
};

export default DriverReturnPickups;
